import styled from '@emotion/styled';
import * as React from 'react';
import { TLCode } from '../../common/enums';
import PharmacySelection from '../common/dropdownSelection/PharmacySelection';
import { TranslatedMessage } from '../common/translation';
import { Instance } from 'mobx-state-tree';
import { TextInput } from '../common/input/basicInputs';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Switch } from '@blueprintjs/core';
import { WithTranslateProps } from '../common/WithTranslate';
import { PharmacyDetails } from '../../store/models';
import { useMemo, useState } from 'react';

interface PharmacySelectionDetailsTransferProps extends WithTranslateProps {
  pharmacyLabelCode: TLCode;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  onChange: (pharmacy: Instance<typeof Pharmacy>) => void;
  getPharmacyById: (id: number) => any;
  pharmacySelected?: Instance<typeof PharmacyDetails>;
  pharmacyLabelColor?: string;
  pharmacyTextColor?: string;
  withEntityAndNbrPharmacy?: boolean;
  nbrPharmacies?: number;
  simpleTransfer?: boolean;
  convertWGS84ToLambert2008?: (lat: number, long: number) => [number, number];
}

export const PharmacySelectionDetailsTransfer = ({
  pharmacyLabelCode,
  pharmacyList,
  onChange,
  getPharmacyById,
  pharmacySelected,
  pharmacyLabelColor,
  pharmacyTextColor,
  withEntityAndNbrPharmacy,
  nbrPharmacies,
  simpleTransfer,
  translate,
  convertWGS84ToLambert2008,
}: PharmacySelectionDetailsTransferProps): JSX.Element => {
  const [displayLambert2008, setDisplayLambert2008] = useState(false);

  const coordinatesLambert2008PharmacySelected = useMemo(() => {
    if (
      pharmacySelected === undefined ||
      pharmacySelected.latitude === undefined ||
      pharmacySelected.longitude === undefined ||
      convertWGS84ToLambert2008 === undefined
    )
      return undefined;

    return convertWGS84ToLambert2008(pharmacySelected.latitude, pharmacySelected.longitude);
  }, [convertWGS84ToLambert2008, pharmacySelected]);

  return (
    <>
      <DivLine>
        <DivColumn style={{ width: '45%' }}>
          <DivLine>
            <DivColumn
              style={{
                color: pharmacyTextColor !== undefined ? pharmacyTextColor : 'black',
                background: pharmacyLabelColor !== undefined ? pharmacyLabelColor : 'white',
                width: '150px',
                paddingLeft: '3px',
              }}
            >
              <TranslatedMessage id={pharmacyLabelCode} />
            </DivColumn>
            <DivColumn style={{ width: '500px' }}>
              <PharmacySelection
                pharmacyList={pharmacyList}
                onChange={onChange}
                pharmacySelected={pharmacySelected}
                getPharmacyById={getPharmacyById}
                showDetails={false}
                showLevel={false}
                showAddress={true}
                showGroup={false}
                fill={true}
                renderNoItemsOnEmptyFilter
              />
            </DivColumn>
          </DivLine>
        </DivColumn>
        <DivColumn style={{ width: '55%' }}>
          <DivLine>
            {displayLambert2008 ? (
              <>
                <DivLine style={{ width: '250px' }}>
                  <span style={{ width: '30%' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_X_Lambert2008} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="latitudeLambert2008"
                    required={true}
                    fill={false}
                    value={
                      coordinatesLambert2008PharmacySelected !== undefined
                        ? Math.round(coordinatesLambert2008PharmacySelected[0]).toString()
                        : ''
                    }
                    disabled
                    style={{ width: '70%' }}
                  />
                </DivLine>
                <DivLine style={{ width: '250px', marginLeft: '20px', marginRight: '4px' }}>
                  <span style={{ width: '30%' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_Y_Lambert2008} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="longitudeLambert2008"
                    required={true}
                    fill={false}
                    value={
                      coordinatesLambert2008PharmacySelected !== undefined
                        ? Math.round(coordinatesLambert2008PharmacySelected[1]).toString()
                        : ''
                    }
                    disabled
                    style={{ width: '70%' }}
                  />
                </DivLine>
              </>
            ) : (
              <>
                <DivLine style={{ width: '250px', marginLeft: '4px' }}>
                  <span style={{ width: '30%', marginRight: '4px' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_Latitude} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="latitude"
                    required={true}
                    fill={false}
                    disabled={true}
                    value={
                      pharmacySelected !== undefined
                        ? pharmacySelected.latitude
                          ? pharmacySelected.latitude!.toString()
                          : ''
                        : ''
                    }
                    style={{ width: '70%' }}
                  />
                </DivLine>
                <DivLine style={{ width: '250px', marginLeft: '4px' }}>
                  <span style={{ width: '30%', marginRight: '4px' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_Longitude} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="longitude"
                    required={true}
                    fill={false}
                    disabled={true}
                    value={
                      pharmacySelected !== undefined
                        ? pharmacySelected.longitude
                          ? pharmacySelected.longitude!.toString()
                          : ''
                        : ''
                    }
                    style={{ width: '70%' }}
                  />
                </DivLine>
              </>
            )}
            {convertWGS84ToLambert2008 && (
              <DivColumn>
                <Switch
                  checked={displayLambert2008}
                  onChange={() => setDisplayLambert2008(!displayLambert2008)}
                  innerLabel={translate(TLCode.TRANSFER_LatLong)}
                  innerLabelChecked={translate(TLCode.TRANSFER_Lambert)}
                />
              </DivColumn>
            )}
            <DivColumn
              style={{ flexDirection: simpleTransfer !== undefined && simpleTransfer ? 'column-reverse' : 'column' }}
            >
              {withEntityAndNbrPharmacy !== undefined && withEntityAndNbrPharmacy == true && (
                <>
                  <DivLine>
                    <DivColumn style={{ marginLeft: '4px', marginRight: '4px' }}>
                      <TranslatedMessage id={TLCode.TRANSFER_PharmacyEntity} />
                    </DivColumn>
                    <DivColumn>
                      <TextInput
                        name="entity"
                        required={true}
                        fill={false}
                        disabled={true}
                        value={
                          pharmacySelected !== undefined ? pharmacySelected.municipality!.mainNameFR.toString() : ''
                        }
                      />
                    </DivColumn>
                    {/* </DivLine>
                &nbsp;&nbsp;
                <DivLine> */}
                    <DivColumn  style={{ marginLeft: '4px', marginRight: '4px' }}>
                      <TranslatedMessage id={TLCode.TRANSFER_PharmacyNbrPharmacies} />
                    </DivColumn>
                    <DivColumn>
                      <TextInput
                        name="nbrPharma"
                        required={true}
                        fill={false}
                        disabled={true}
                        value={nbrPharmacies !== undefined ? nbrPharmacies.toString() : ''}
                        style={{
                          backgroundColor:
                            nbrPharmacies !== undefined && nbrPharmacies > 1
                              ? 'lightgreen'
                              : nbrPharmacies == 1
                              ? 'red'
                              : 'light-grey',
                          color: 'black',
                          width: '40%',
                        }}
                      />
                    </DivColumn>
                  </DivLine>
                </>
              )}
              {/* {simpleTransfer !== undefined && simpleTransfer ? (
                <DivColumn>
                  <DivLine style={{ justifyContent: 'flex-end', marginLeft: '5px' }}>
                    <Button text={translate(TLCode.TRANSFER_Explore)} intent={Intent.PRIMARY} onClick={onExplore} />
                  </DivLine>
                </DivColumn>
              ) : (
                <></>
              )} */}
            </DivColumn>
          </DivLine>
        </DivColumn>
      </DivLine>
    </>
  );
};

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
